import { Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ExternalRequisition } from '../../../models/requisitions/interfaces/external-requisition';
import { ExternalRequisitionSearch } from '../../../models/requisitions/interfaces/external-requisition-search';
import { Department } from '../../../models/department/department';
import { ExternalRequisitionStatus } from '../../../models/requisitions/interfaces/external-requisition-status';
import { TreeNode } from 'primeng/api';
import { ItemMaster } from '../../../models/item/item-master/item-master';
import { DynamicFilterConfig, DynamicFilterState, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';
import { Observable, Subject, forkJoin, finalize, map, of, takeUntil } from 'rxjs';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { EnumService } from '../../../services/enum.service';
import { UserFull } from '../../../models/user-master/user-lite';
import { TableLazyLoadEvent } from 'primeng/table';
import { TreeTableLazyLoadEvent } from 'primeng/treetable';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import {
  ProcurementItemCategory,
  ProcurementItemCategoryProxyService,
} from '../../../proxies/order/procurement-item-category.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ReportsProxyService } from '../../../proxies/order/reports.proxy.service';
import { RequisitionStatusProxyService } from '../../../proxies/order/requisition-status.proxy.service';
import { RequisitionProxyService } from '../../../proxies/order/requisition.proxy.service';
import { DepartmentProxyService } from '../../../proxies/user-master/department.proxy.service';
import { UserProxyService } from '../../../proxies/user-master/user.proxy.service';
import { RequisitionService } from '../../../services/requisition.service';

import { ExternalRequisitionAdminGroup } from '../../../models/roles/roles-group';
import { AuthService } from '@card-software/nexus-utilities';
interface DateRangeForm {
  startDate: FormControl<Date | null>;
  endDate: FormControl<Date | null>;
}

@Component({
  selector: 'app-requisitions',
  templateUrl: './requisitions.component.html',
  styleUrl: './requisitions.component.scss',
})
export class RequisitionsComponent implements OnInit, OnDestroy {
  /** `?tab=` value per section, so a reload or a shared link reopens the same tab. */
  private static readonly TAB_PARAMS: Record<number, string> = {
    4: 'overview',
    1: 'mine-all',
    2: 'pending-approval',
    5: 'reports',
  };

  isLoading = 0;
  section: { label: string; value: number }[] = [];
  requisitionList: ExternalRequisition[] = [];
  requisitionPendingList: ExternalRequisition[] = [];
  allDepartments: Department[] = [];
  allRequisitionStatus: ExternalRequisitionStatus[] = [];
  selectedSection = new FormControl(4);
  requisitions: TreeNode<ExternalRequisition>[] = [];
  ExternalRequisitionReportForm = new FormGroup<DateRangeForm>({
    startDate: new FormControl<Date | null>(null, [Validators.required]),
    endDate: new FormControl<Date | null>(null, [Validators.required]),
  });

  // External Requisitions tab signals and properties
  departments = signal<(Department & { fieldToDisplay?: string })[]>([]);
  filteredDepartments = signal<(Department & { fieldToDisplay?: string })[]>([]);
  procurementCategories = signal<(ProcurementItemCategory & { fieldToDisplay?: string })[]>([]);
  filteredCategories = signal<(ProcurementItemCategory & { fieldToDisplay?: string })[]>([]);
  items = signal<(ItemMaster & { fieldToDisplay?: string })[]>([]);
  filteredItems = signal<(ItemMaster & { fieldToDisplay?: string })[]>([]);
  users = signal<(UserFull & { fieldToDisplay?: string })[]>([]);
  filteredUsers = signal<(UserFull & { fieldToDisplay?: string })[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  // Mine(All) is already scoped to createdBy = current user, so that filter is redundant there.
  mineAllFilterConfig = computed<DynamicFilterConfig | null>(() => {
    const config = this.filterConfig();
    if (!config) return null;
    return { ...config, filters: config.filters.filter((filter) => filter.formControlName !== 'createdBy') };
  });
  private readonly activeSection = signal<number | null>(null);
  readonly mineAllFilterState = createDynamicFilterState({
    storageKey: 'mine-all-requisitions-filters',
    config: () => (this.activeSection() === 1 ? this.mineAllFilterConfig() : null),
    paramPrefix: 'mineAll.',
  });
  readonly minePendingFilterState = createDynamicFilterState({
    storageKey: 'mine-pending-approval-requisitions-filters',
    config: () => (this.activeSection() === 2 ? this.filterConfig() : null),
    paramPrefix: 'minePending.',
  });
  readonly externalFilterState = createDynamicFilterState({
    storageKey: 'external-requisitions-filters',
    config: () => (this.activeSection() === 4 ? this.filterConfig() : null),
    paramPrefix: 'ext.',
  });
  private readonly filterStates = new Map<number, DynamicFilterState>([
    [1, this.mineAllFilterState],
    [2, this.minePendingFilterState],
    [4, this.externalFilterState],
  ]);
  private readonly hydratedFilterStates = new Set<DynamicFilterState>();
  private filtersPanelInitialized = false;
  externalRequisitionsList = signal<ExternalRequisition[]>([]);
  uomValues = signal<Record<string, string>>({});

  // Server-side pagination state for General Overview
  totalRecords = 0;
  currentSearchCriteria: ExternalRequisitionSearch = {};
  currentSortField: string | undefined;
  currentSortOrder = 0;

  // Location options for filter
  locationOptions = [
    { label: 'Consumables', value: 'NT.WH-CON-1' },
    { label: 'Chemicals', value: 'NT.WH-LD-1' },
  ];

  private destroy$ = new Subject<void>();
  private filtersInitialized = false;
  private statusesLoaded = false;
  private filterDataLoaded = false;

  // Filters panel visibility
  showFilters = signal(false);

  // Column visibility for external requisitions table
  availableColumns: { label: string; value: string }[] = [];
  selectedColumns: string[] = [];

  isColumnVisible(column: string): boolean {
    return this.selectedColumns.includes(column);
  }

  get visibleColumnCount(): number {
    return this.selectedColumns.length;
  }

  // Pagination properties
  rows = 50;
  rowsPerPageOptions = [20, 30, 50, 100];

  // Server-side pagination state for Mine(All) / Mine(Pending Approval)
  mineTotalRecords = 0;
  mineFirst = 0;
  mineRows = this.rows;
  private mineSearchCriteria: ExternalRequisitionSearch = {};

  get headerTitle(): string {
    return this.translateService.instant('Requisition_Title');
  }

  get isRequisitionAdmin(): boolean {
    return this.userAuthenticationService.roles.includes(ExternalRequisitionAdminGroup.ExternalRequisitionAdmin);
  }

  get isExternalRequisitionsTab(): boolean {
    return this.selectedSection.value === 4;
  }

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private requisitionService = inject(RequisitionProxyService);
  private statusRequisitionService = inject(RequisitionStatusProxyService);
  private reportProxyService = inject(ReportsProxyService);
  private departmentService = inject(DepartmentProxyService);
  private procurementCategoryService = inject(ProcurementItemCategoryProxyService);
  private itemMasterService = inject(ItemMasterProxyService);
  private localeHelperService = inject(LocaleHelperService);
  private enumService = inject(EnumService);
  private userProxyService = inject(UserProxyService);
  private tabState = inject(RequisitionService);

  constructor() {
    this.filterStates.forEach((state, section) => {
      state.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
        this.hydratedFilterStates.add(state);
        if (this.activeSection() !== section) return;
        if (!this.filtersPanelInitialized) {
          this.filtersPanelInitialized = true;
          this.showFilters.set(state.hasActive());
        }
        this.searchSection(section, filters);
      });
    });

    this.selectedColumns = [
      'number',
      'location',
      'createdDate',
      'department',
      'priority',
      'status',
      'issuedDate',
      'poNumbers',
      'approvedItems',
      'type',
      'category',
      'assignedUser',
      'approvers',
      'purchasingDone',
      'actions',
    ];

    // The `?tab=` param wins (reload, shared link). Without it, restores whichever
    // tab was active before this component was last destroyed (e.g. navigating to
    // Requisition Details and back via Cancel/Save, which is a real route change
    // and would otherwise reset selectedSection to its hardcoded default).
    // General Overview (4) and Reports (5) are admin-only tabs, so anyone else
    // starts on Mine(All) instead of on a tab the selector doesn't offer.
    const activeTab = this.sectionFromTabParam(this.route.snapshot.queryParamMap.get('tab')) ?? this.tabState.activeTab();
    this.selectedSection.setValue(this.isRequisitionAdmin || activeTab < 4 ? activeTab : 1, { emitEvent: false });
    this.activeSection.set(this.selectedSection.value);
    this.tabState.activeTab.set(this.selectedSection.value ?? 4);

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.initializeLabels();
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }
  ngOnInit(): void {
    this.loadRequisitionStatuses();
    this.loadFilterData();
    this.syncTabParam(this.selectedSection.value);
    this.selectedSection.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tabState.activeTab.set(this.selectedSection.value ?? 4);
      this.syncTabParam(this.selectedSection.value);
      this.activeSection.set(this.selectedSection.value);
      if (this.selectedSection.value === 4) {
        this.tryInitializeFilterConfig();
      }
      this.searchActiveSection();
    });
  }

  private sectionFromTabParam(tab: string | null): number | null {
    const entry = Object.entries(RequisitionsComponent.TAB_PARAMS).find(([, param]) => param === tab);
    return entry ? Number(entry[0]) : null;
  }

  // Runs before the tab's filter state mounts: the state waits for an in-flight
  // navigation before rewriting its own params, so neither navigation drops the other.
  private syncTabParam(section: number | null): void {
    const tab = section === null ? undefined : RequisitionsComponent.TAB_PARAMS[section];
    if (!tab || this.route.snapshot.queryParamMap.get('tab') === tab) return;
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  private searchActiveSection(): void {
    const section = this.activeSection();
    const state = section === null ? undefined : this.filterStates.get(section);
    if (section === null || !state || !this.hydratedFilterStates.has(state)) return;
    this.searchSection(section, state.applied());
  }

  private searchSection(section: number, filters: DynamicFilterValues): void {
    const searchCriteria = this.buildSearchCriteriaFromFilters(filters);
    if (section === 4) {
      this.searchExternalRequisitions(searchCriteria);
    } else {
      this.searchRequisitions(searchCriteria);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRequisitionStatuses(): void {
    this.statusRequisitionService
      .getAllStatuses()
      .pipe(
        finalize(() => {
          this.statusesLoaded = true;
          this.tryInitializeFilterConfig();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.allRequisitionStatus = response.data;
          }
        },
        error: (error) => {
          console.error('Error loading requisition statuses:', error);
        },
      });
  }

  private loadFilterData(): void {
    this.isLoading++;
    forkJoin([
      this.departmentService.getAllDepartments(),
      this.procurementCategoryService.getAllItemsCategories(),
      this.userProxyService.getAllUsers(),
    ])
      .pipe(
        finalize(() => {
          this.isLoading--;
          this.filterDataLoaded = true;
          this.tryInitializeFilterConfig();
        }),
      )
      .subscribe({
        next: ([departmentsRes, categoriesRes, usersRes]) => {
          const depts = (departmentsRes.data ?? []).map((dept) => ({
            ...dept,
            fieldToDisplay: this.localeHelperService.getLocalizedValue(dept.name) || '',
          }));
          this.departments.set(depts);
          this.filteredDepartments.set(depts);

          const cats = (categoriesRes.data ?? []).map((cat) => ({
            ...cat,
            fieldToDisplay: this.localeHelperService.getLocalizedValue(cat.name) || '',
          }));
          this.procurementCategories.set(cats);
          this.filteredCategories.set(cats);

          const allUsers = (usersRes.data ?? []).map((user) => ({
            ...user,
            fieldToDisplay: `${user.profile.firstName} ${user.profile.lastName} (${user.username})`,
          }));
          this.users.set(allUsers);
          this.filteredUsers.set(allUsers);

          const uoms = this.enumService.getEnum('UnitOfMeasure', 'itemEnums')?.values || {};
          this.uomValues.set(uoms as Record<string, string>);
        },
        error: (error) => {
          console.error('Error loading filter data:', error);
        },
      });
  }

  private initializeLabels(): void {
    this.section = [
      ...(this.isRequisitionAdmin ? [{ label: this.translateService.instant('General Overview'), value: 4 }] : []),
      { label: this.translateService.instant('Mine(All)'), value: 1 },
      { label: this.translateService.instant('Mine(Pending Approval)'), value: 2 },
      ...(this.isRequisitionAdmin ? [{ label: this.translateService.instant('Requisition Reports'), value: 5 }] : []),
    ];

    this.availableColumns = [
      { label: this.translateService.instant('Requisition_Number'), value: 'number' },
      { label: this.translateService.instant('Requisition_Location'), value: 'location' },
      { label: this.translateService.instant('Requisition_Date_Created'), value: 'createdDate' },
      { label: this.translateService.instant('Requisition_Department'), value: 'department' },
      { label: this.translateService.instant('Requisition_Priority'), value: 'priority' },
      { label: this.translateService.instant('Requisition_Status'), value: 'status' },
      { label: this.translateService.instant('Requisition_Issued_Date'), value: 'issuedDate' },
      { label: this.translateService.instant('Requisition_PO_Numbers'), value: 'poNumbers' },
      { label: this.translateService.instant('Requisition_Approved_Items'), value: 'approvedItems' },
      { label: this.translateService.instant('Requisition_Type'), value: 'type' },
      { label: this.translateService.instant('Requisition_Category'), value: 'category' },
      { label: this.translateService.instant('Requisition_Assigned_User'), value: 'assignedUser' },
      { label: this.translateService.instant('Requisition_Approvers'), value: 'approvers' },
      { label: this.translateService.instant('Requisition_Purchasing_Done'), value: 'purchasingDone' },
      { label: this.translateService.instant('Actions'), value: 'actions' },
    ];
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.filterDataLoaded && this.statusesLoaded && !this.filtersInitialized) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('Requisition_Number', 'Requisition Number'),
          placeholder: translate('Requisition_Number_Placeholder', 'Enter requisition number'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'statusReference',
          label: translate('Requisition_Status', 'Status'),
          placeholder: translate('Requisition_Status_Placeholder', 'All Statuses'),
          options: this.allRequisitionStatus.map((status) => ({
            label: this.localeHelperService.getLocalizedValue(status.displayName) || '',
            value: status.id || '',
          })),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'destinationLocationCode',
          label: translate('Requisition_Location', 'Location'),
          placeholder: translate('Requisition_Location_Placeholder', 'All Locations'),
          options: this.locationOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'departmentReference',
          label: translate('Requisition_Department', 'Department'),
          placeholder: translate('Search_Department', 'Select Department'),
          options: this.departments().map((dept) => ({
            label: dept.fieldToDisplay || '',
            value: dept.id || '',
          })),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'createdBy',
          label: translate('Requisition_Created_By', 'Created By'),
          placeholder: translate('Requisition_Created_By_Placeholder', 'Search user'),
          autocompleteConfig: {
            suggestions: this.filteredUsers(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'username',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterUsers(event),
            resolveById: (id: string) => this.resolveUser(id),
            dropdown: false,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'assignedTo',
          label: translate('Requisition_Assigned_To', 'Assigned To'),
          placeholder: translate('Requisition_Assigned_To_Placeholder', 'Search user'),
          autocompleteConfig: {
            suggestions: this.filteredUsers(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'username',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterUsers(event),
            resolveById: (id: string) => this.resolveUser(id),
            dropdown: false,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'itemCode',
          label: translate('Requisition_Item', 'Item'),
          placeholder: translate('Requisition_Item_Placeholder', 'Item code or name'),
          autocompleteConfig: {
            suggestions: this.filteredItems(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'itemCode',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterItems(event),
            resolveById: (id: string) => this.resolveItem(id),
            dropdown: false,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'requisitionProcurementCategoryReference',
          label: translate('Requisition_Category', 'Procurement Category'),
          placeholder: translate('Search_Category', 'Select Category'),
          options: this.procurementCategories().map((cat) => ({
            label: cat.fieldToDisplay || '',
            value: cat.id || '',
          })),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'date',
          formControlName: 'fromCreatedDate',
          label: translate('Requisition_From_Date', 'From Date'),
          placeholder: translate('Requisition_From_Date_Placeholder', 'Start date'),
          showClear: true,
          defaultVisible: false,
          rangeEnd: 'toCreatedDate',
        },
        {
          type: 'date',
          formControlName: 'toCreatedDate',
          label: translate('Requisition_To_Date', 'To Date'),
          placeholder: translate('Requisition_To_Date_Placeholder', 'End date'),
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'checkbox',
          formControlName: 'purchasingDepartmentDone',
          label: translate('Requisition_Purchasing_Done', 'Purchasing Dept. Done'),
          defaultVisible: false,
        },
        {
          type: 'checkbox',
          formControlName: 'hasRelatedPurchaseOrders',
          label: translate('Requisition_Has_POs', 'Has Related POs'),
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'fromApprovedDate',
          label: translate('Requisition_From_Approved_Date', 'From Approved Date'),
          placeholder: translate('Requisition_From_Approved_Date_Placeholder', 'Start date'),
          showClear: true,
          defaultVisible: false,
          rangeEnd: 'toApprovedDate',
        },
        {
          type: 'date',
          formControlName: 'toApprovedDate',
          label: translate('Requisition_To_Approved_Date', 'To Approved Date'),
          placeholder: translate('Requisition_To_Approved_Date_Placeholder', 'End date'),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
    this.filtersInitialized = true;
  }

  private resolveUser(username: string): Observable<(UserFull & { fieldToDisplay?: string }) | null> {
    return of(this.users().find((user) => user.username === username) ?? null);
  }

  private resolveItem(itemCode: string): Observable<(ItemMaster & { fieldToDisplay?: string }) | null> {
    return this.itemMasterService.getItemCodesBatch({ itemCodes: [itemCode] }).pipe(
      map((response) => {
        const item = (response.data ?? []).find((candidate) => candidate.itemCode === itemCode);
        return item
          ? { ...item, fieldToDisplay: `(${item.itemCode}) ${this.localeHelperService.getLocalizedValue(item.itemDisplayName) || ''}` }
          : null;
      }),
    );
  }

  filterDepartments(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.toLowerCase() || '';
    const filtered = this.departments().filter((dept) => {
      const localizedName = this.localeHelperService.getLocalizedValue(dept.name)?.toLowerCase() || '';
      return localizedName.includes(query);
    });
    this.filteredDepartments.set(filtered);
  }

  filterCategories(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.toLowerCase() || '';
    const filtered = this.procurementCategories().filter((cat) => {
      const localizedName = this.localeHelperService.getLocalizedValue(cat.name)?.toLowerCase() || '';
      return localizedName.includes(query);
    });
    this.filteredCategories.set(filtered);
  }

  filterUsers(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.toLowerCase() || '';
    const filtered = this.users().filter((user) => {
      const displayName = user.fieldToDisplay?.toLowerCase() || '';
      return displayName.includes(query);
    });
    this.filteredUsers.set(filtered);
    this.updateUserSuggestions();
  }

  filterItems(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim() || '';
    if (query.length < 2) {
      this.filteredItems.set([]);
      this.updateItemSuggestions();
      return;
    }

    this.itemMasterService.postItemSearch({ name: query, itemCode: query }, true).subscribe({
      next: (response) => {
        const items = (response.data?.items ?? []).map((item) => ({
          ...item,
          fieldToDisplay: `(${item.itemCode}) ${this.localeHelperService.getLocalizedValue(item.itemDisplayName) || ''}`,
        }));
        this.filteredItems.set(items);
        this.updateItemSuggestions();
      },
      error: () => {
        this.filteredItems.set([]);
        this.updateItemSuggestions();
      },
    });
  }

  private updateItemSuggestions(): void {
    this.updateAutocompleteSuggestions('itemCode', this.filteredItems());
  }

  private updateUserSuggestions(): void {
    this.updateAutocompleteSuggestions('createdBy', this.filteredUsers());
    this.updateAutocompleteSuggestions('assignedTo', this.filteredUsers());
  }

  private updateAutocompleteSuggestions(formControlName: string, suggestions: unknown[]): void {
    const currentConfig = this.filterConfig();
    if (!currentConfig) return;

    const updatedFilters = currentConfig.filters.map((filter) => {
      if (filter.formControlName === formControlName && filter.autocompleteConfig) {
        return {
          ...filter,
          autocompleteConfig: {
            ...filter.autocompleteConfig,
            suggestions,
          },
        };
      }
      return filter;
    });

    this.filterConfig.set({
      ...currentConfig,
      filters: updatedFilters,
    });
  }

  searchExternalRequisitions(searchCriteria: ExternalRequisitionSearch): void {
    this.currentSearchCriteria = searchCriteria;

    this.isLoading++;
    this.requisitionService
      .searchPaginatedRequisitions(searchCriteria, false, false, 1, this.rows)
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next: (response) => {
          const paginated = response.data;
          this.externalRequisitionsList.set(paginated?.items || []);
          this.totalRecords = paginated?.totalCount || 0;
        },
        error: (error) => {
          console.error('Error loading external requisitions:', error);
          this.externalRequisitionsList.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor((event.first || 0) / (event.rows || this.rows)) + 1;
    const pageSize = event.rows || this.rows;

    const sortField = event.sortField as string | undefined;
    const sortDirection = event.sortOrder === 1 ? 'asc' : event.sortOrder === -1 ? 'desc' : undefined;

    const searchCriteria: ExternalRequisitionSearch = {
      ...this.currentSearchCriteria,
      sortBy: sortField,
      sortDirection,
    };

    this.isLoading++;
    this.requisitionService
      .searchPaginatedRequisitions(searchCriteria, false, false, pageNumber, pageSize)
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next: (response) => {
          const paginated = response.data;
          this.externalRequisitionsList.set(paginated?.items || []);
          this.totalRecords = paginated?.totalCount || 0;
        },
        error: (error) => {
          console.error('Error loading external requisitions:', error);
          this.externalRequisitionsList.set([]);
          this.totalRecords = 0;
        },
      });
  }

  private buildSearchCriteriaFromFilters(filters: DynamicFilterValues): ExternalRequisitionSearch {
    const extractValue = (val: unknown, key: string): string | undefined => {
      if (!val) return undefined;
      return typeof val === 'object' ? (val as Record<string, string>)[key] : (val as string);
    };

    return {
      number: (filters['number'] as string)?.trim() || undefined,
      statusReference: (filters['statusReference'] as string) || undefined,
      departmentReference: (filters['departmentReference'] as string) || undefined,
      itemCode: extractValue(filters['itemCode'], 'itemCode'),
      requisitionProcurementCategoryReference: (filters['requisitionProcurementCategoryReference'] as string) || undefined,
      createdBy: extractValue(filters['createdBy'], 'username'),
      assignedTo: extractValue(filters['assignedTo'], 'username'),
      destinationLocationCode: (filters['destinationLocationCode'] as string) || undefined,
      fromCreatedDate: (filters['fromCreatedDate'] as Date) || undefined,
      toCreatedDate: (filters['toCreatedDate'] as Date) || undefined,
      purchasingDepartmentDone: (filters['purchasingDepartmentDone'] as boolean) ?? undefined,
      hasRelatedPurchaseOrders: (filters['hasRelatedPurchaseOrders'] as boolean) ?? undefined,
      fromApprovedDate: (filters['fromApprovedDate'] as Date) || undefined,
      toApprovedDate: (filters['toApprovedDate'] as Date) || undefined,
    };
  }

  getUomLabel(uomKey: number): string {
    return this.uomValues()[uomKey] || '';
  }

  formatQuantity(quantity: number): string {
    return quantity?.toLocaleString() || '0';
  }

  getApprovedItems(entries: ExternalRequisition['entries']): string {
    if (!entries || entries.length === 0) return '-';
    const approvedEntries = entries.filter((entry) => entry.approved);
    if (approvedEntries.length === 0) return '-';
    return approvedEntries
      .map((entry) => {
        const displayName = this.localeHelperService.getLocalizedValue(entry.itemDisplayName) || entry.itemCode;
        const uom = this.getUomLabel(entry.uom);
        return `(${entry.itemCode}) ${displayName} [${uom}]: ${entry.quantity}`;
      })
      .join(', ');
  }

  getPoNumbers(requisition: ExternalRequisition): string {
    if (!requisition.relatedPurchaseOrders || requisition.relatedPurchaseOrders.length === 0) return '-';
    return requisition.relatedPurchaseOrders.map((po) => po.purchaseOrderNumber).join(', ');
  }

  getRequisitionTypeLabel(type: number): string {
    const types: Record<number, string> = {
      0: 'International',
      1: 'Local',
    };
    return types[type] || '-';
  }

  getPriorityLabel(priority: number): string {
    const priorities: Record<number, string> = {
      0: 'Standard',
      1: 'High',
      2: 'Urgent',
    };
    return priorities[priority] || '-';
  }

  getCategoryName(requisition: ExternalRequisition): string {
    if (!requisition.requisitionProcurementCategory) return '-';
    return this.localeHelperService.getLocalizedValue(requisition.requisitionProcurementCategory.name) || '-';
  }

  getApproversInfo(approvers: ExternalRequisition['approvers']): string {
    if (!approvers || approvers.length === 0) return '-';
    return approvers
      .map((approver) => {
        const name = approver.userLite ? `${approver.userLite.firstName} ${approver.userLite.lastName}` : approver.username || '';
        const date = approver.approvedAt ? new Date(approver.approvedAt).toLocaleDateString() : '';
        return date ? `${name} (${date})` : name;
      })
      .join(', ');
  }

  getLocationLabel(requisition: ExternalRequisition): string {
    if (requisition.destinationLocationName) {
      return this.localeHelperService.getLocalizedValue(requisition.destinationLocationName) || requisition.destinationLocationCode || '-';
    }
    // Fallback to code label
    const location = this.locationOptions.find((loc) => loc.value === requisition.destinationLocationCode);
    return location?.label || requisition.destinationLocationCode || '-';
  }

  searchRequisitions(searchCriteria: ExternalRequisitionSearch = {}): void {
    this.mineSearchCriteria = searchCriteria;
    this.mineFirst = 0;
    this.loadMineRequisitions(1, this.mineRows);
  }

  onMineLazyLoad(event: TreeTableLazyLoadEvent): void {
    this.mineRows = event.rows || this.mineRows;
    this.mineFirst = event.first || 0;
    this.loadMineRequisitions(Math.floor(this.mineFirst / this.mineRows) + 1, this.mineRows);
  }

  private loadMineRequisitions(pageNumber: number, pageSize: number): void {
    const currentUser = this.userAuthenticationService.userLite;
    const searchType = this.selectedSection.value;
    let method;

    switch (searchType) {
      case 1:
        // Mine(All) is always scoped to the current user, regardless of filter panel input.
        method = this.requisitionService.searchPaginatedRequisitions(
          { ...this.mineSearchCriteria, createdBy: currentUser?.username || '' },
          false,
          false,
          pageNumber,
          pageSize,
          true,
        );
        break;
      case 2:
        method = this.requisitionService.searchPaginatedRequisitions(this.mineSearchCriteria, true, false, pageNumber, pageSize, true);
        break;
      default:
        // Requisition Reports (5) and any other tab don't drive this tree-table.
        return;
    }

    this.isLoading++;
    method.subscribe({
      next: (results) => {
        this.requisitionList = results.data?.items || [];
        this.mineTotalRecords = results.data?.totalCount || 0;
        this.populateTreeNodes();
        this.isLoading--;
      },
      error: (error) => {
        this.isLoading--;
        console.error('Error loading data', error);
      },
    });
  }

  get canCreateRequisition(): boolean {
    return AuthService.hasRole(ExternalRequisitionAdminGroup.ExternalRequisitionAdmin);
  }

  viewRequisitionDetails(id?: string): void {
    console.log('ViewRequisitionDetails', id);
    this.router.navigate(['/requisition-details'], { queryParams: { id: id ?? 'new' } });
  }

  populateTreeNodes(): void {
    // Group by the real parent/child relationship (parentRequisitionReference -> id),
    // not by parsing the requisition number, since the number format's segment count
    // isn't a reliable indicator of a child requisition (e.g. "EXT-REQ-NT-1217").
    const nodeById = new Map<string, TreeNode<ExternalRequisition>>();

    this.requisitionList.forEach((requisition) => {
      if (!requisition.id) return;
      nodeById.set(requisition.id, { data: requisition, children: [] });
    });

    const roots: TreeNode<ExternalRequisition>[] = [];

    this.requisitionList.forEach((requisition) => {
      if (!requisition.id) return;
      const node = nodeById.get(requisition.id)!;
      const parentNode = requisition.parentRequisitionReference ? nodeById.get(requisition.parentRequisitionReference) : undefined;

      if (parentNode) {
        parentNode.children?.push(node);
      } else {
        // No parent in this result set (either it has none, or the parent wasn't
        // returned by the current search/page) - show it as its own root row.
        roots.push(node);
      }
    });

    this.requisitions = roots;
  }

  getRequisitionStrict(requisition: unknown): ExternalRequisition {
    return requisition as ExternalRequisition;
  }

  getStrictTreeNodeRequisition(node: unknown): TreeNode<ExternalRequisition> {
    return node as TreeNode<ExternalRequisition>;
  }
  generateExternalRequisitionReport(): void {
    if (!this.ExternalRequisitionReportForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }

    const startDate = this.ExternalRequisitionReportForm.value.startDate;
    const endDate = this.ExternalRequisitionReportForm.value.endDate;

    this.reportProxyService
      .getExternalRequisitionReport({
        fromDate: startDate!,
        toDate: endDate!,
      })
      .pipe()
      .subscribe({
        next: (blob) => {
          const filename = `External_Requisition_${startDate!.toISOString().split('T')[0]}_to_${endDate!.toISOString().split('T')[0]}.xlsx`;
          this.downloadBlob(blob, filename);
          GlobalMessageUtil.success('Success', 'External approved requisition report generated successfully');
        },
      });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  downloadRequisitionPdf(requisitionId: string, event: Event): void {
    event.stopPropagation();
    if (!requisitionId) {
      GlobalMessageUtil.error('Error', 'No requisition selected');
      return;
    }
    this.isLoading++;
    this.requisitionService.downloadRequisitionPdf(requisitionId).subscribe({
      next: (blob: Blob) => {
        this.isLoading--;
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      },
      error: (error) => {
        console.error('Error downloading requisition PDF:', error);
        this.isLoading--;
      },
    });
  }
}
